/**
 * Checkout BFF Client
 *
 * Client-side functions for fetching checkout data via the BFF API.
 * This replaces direct Saleor calls with calls to /api/checkout/[id].
 *
 * Benefits:
 * - Server handles GraphQL complexity
 * - Saleor endpoints not exposed in browser network tab
 * - Type-safe responses
 * - Request deduplication via orchestrator cache
 */

import type { CheckoutPageData } from '@/lib/checkoutData/types';
import type { CheckoutData } from '@/lib/checkout/types';
import type { ApiSuccessResponse, ApiErrorResponse } from '@/lib/api/schemas';
import { devError } from '@/lib/utils/devLog';

/**
 * Result of a checkout fetch operation
 */
export interface CheckoutFetchResult {
  checkout: CheckoutData | null;
  isExpired: boolean;
  deliveryMethods?: CheckoutPageData['deliveryMethods'];
  error?: string;
}

/**
 * API response type for checkout endpoint
 */
type CheckoutApiResponse = ApiSuccessResponse<CheckoutPageData> | ApiErrorResponse;

/**
 * Fetch checkout data via BFF API
 *
 * @param checkoutId - The checkout ID to fetch
 * @param locale - The locale (e.g., 'us-en')
 * @returns Checkout data or error state
 */
export async function fetchCheckoutFromBFF(
  checkoutId: string,
  locale: string
): Promise<CheckoutFetchResult> {
  try {
    const response = await fetch(`/api/checkout/${checkoutId}?lang=${locale}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      // Important: Include credentials for cookie-based auth if needed
      credentials: 'same-origin',
    });

    const json = (await response.json()) as CheckoutApiResponse;

    if (!json.success) {
      // Handle standardized error response
      const errorCode = json.errors?.[0]?.code || 'UNKNOWN_ERROR';
      const errorMessage = json.errors?.[0]?.message || 'Unknown error';

      return {
        checkout: null,
        isExpired: errorCode === 'NOT_FOUND' || response.status === 404,
        error: errorMessage,
      };
    }

    return {
      checkout: json.data?.checkout || null,
      isExpired: false,
      deliveryMethods: json.data?.deliveryMethods,
    };
  } catch (error) {
    devError('[CheckoutClient] Failed to fetch checkout:', error);
    return {
      checkout: null,
      isExpired: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Check if a checkout exists via BFF API (lightweight check)
 *
 * @param checkoutId - The checkout ID to check
 * @param locale - The locale
 * @returns true if checkout exists and is valid
 */
export async function checkCheckoutExists(checkoutId: string, locale: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/checkout/${checkoutId}?lang=${locale}`, {
      method: 'HEAD',
      credentials: 'same-origin',
    });
    return response.ok;
  } catch {
    return false;
  }
}
